// Proof-of-work solver for the sign-up form (see Auth::PowChallengeConcern).
// Finds a nonce such that SHA256("<seed>:<nonce>") starts with `difficulty`
// leading zero hex characters, fills the hidden field and re-enables submit.
// Runs on the main thread in yielding batches to keep the UI responsive — at
// the default difficulty this completes in well under a second.

import ready from '../mastodon/ready';

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const encoder = new TextEncoder();

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

function sha256Hex(message: string): string {
  const msg = encoder.encode(message);
  const bitLen = msg.length * 8;
  const totalLen = ((msg.length + 1 + 8 + 63) >> 6) << 6;
  const buf = new Uint8Array(totalLen);
  buf.set(msg);
  buf[msg.length] = 0x80;

  const hi = Math.floor(bitLen / 0x100000000);
  const lo = bitLen >>> 0;
  buf[totalLen - 8] = (hi >>> 24) & 0xff;
  buf[totalLen - 7] = (hi >>> 16) & 0xff;
  buf[totalLen - 6] = (hi >>> 8) & 0xff;
  buf[totalLen - 5] = hi & 0xff;
  buf[totalLen - 4] = (lo >>> 24) & 0xff;
  buf[totalLen - 3] = (lo >>> 16) & 0xff;
  buf[totalLen - 2] = (lo >>> 8) & 0xff;
  buf[totalLen - 1] = lo & 0xff;

  let h0 = 0x6a09e667;
  let h1 = 0xbb67ae85;
  let h2 = 0x3c6ef372;
  let h3 = 0xa54ff53a;
  let h4 = 0x510e527f;
  let h5 = 0x9b05688c;
  let h6 = 0x1f83d9ab;
  let h7 = 0x5be0cd19;

  const w = new Uint32Array(64);

  for (let off = 0; off < totalLen; off += 64) {
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4;
      w[i] =
        ((buf[j] << 24) | (buf[j + 1] << 16) | (buf[j + 2] << 8) | buf[j + 3]) >>>
        0;
    }
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    let f = h5;
    let g = h6;
    let h = h7;

    for (let i = 0; i < 64; i++) {
      const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + s1 + ch + K[i] + w[i]) >>> 0;
      const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (s0 + maj) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
    h5 = (h5 + f) >>> 0;
    h6 = (h6 + g) >>> 0;
    h7 = (h7 + h) >>> 0;
  }

  const hex = (n: number) => n.toString(16).padStart(8, '0');
  return (
    hex(h0) + hex(h1) + hex(h2) + hex(h3) + hex(h4) + hex(h5) + hex(h6) + hex(h7)
  );
}

async function solve(seed: string, difficulty: number): Promise<string> {
  const prefix = '0'.repeat(difficulty);
  let nonce = 0;

  for (;;) {
    for (let i = 0; i < 5000; i++) {
      const candidate = String(nonce);
      if (sha256Hex(`${seed}:${candidate}`).startsWith(prefix)) {
        return candidate;
      }
      nonce++;
    }
    // Yield to the event loop so the page stays responsive while solving.
    await new Promise((resolve) => {
      setTimeout(resolve, 0);
    });
  }
}

ready(() => {
  const container = document.querySelector<HTMLElement>('.antibot-pow');
  if (!container) return;

  const seed = container.dataset.seed;
  const difficulty = parseInt(container.dataset.difficulty ?? '0', 10);
  const nonceField =
    document.querySelector<HTMLInputElement>('#antibot_pow_nonce');

  if (!seed || !difficulty || !nonceField) return;

  const form = container.closest('form');
  const submit = form?.querySelector<HTMLButtonElement>('button[type=submit]');
  const status = document.querySelector<HTMLElement>('#antibot-pow-status');

  if (submit) submit.disabled = true;
  if (status) status.textContent = status.dataset.solving ?? '';

  solve(seed, difficulty)
    .then((nonce) => {
      nonceField.value = nonce;
      if (submit) submit.disabled = false;
      if (status) status.textContent = status.dataset.done ?? '';
      return undefined;
    })
    .catch(() => {
      // Never lock a real user out on a solver error — let the server decide.
      if (submit) submit.disabled = false;
    });
}).catch((e: unknown) => {
  throw e;
});
